import { z } from '../common/zod-openapi'
import { bzOptional } from '../domain/common-schemas'
import { guidSchema, htmlStringSchema, isoDateStringSchema, timeZoneIdSchema, uscSchema } from './_common'
import { TaxConfigurationSchema } from './TaxConfigurations'

// PDF Address schema (renamed to avoid conflict with domain Address)
export const pdfAddressSchema = z
  .object({
    line1: z.string().openapi({ example: '123 Main Street' }),
    line2: bzOptional(z.string().openapi({ example: 'Suite 100' })),
    city: z.string().openapi({ example: 'San Francisco' }),
    stateAbbreviation: z.string().openapi({ example: 'CA' }),
    zipCode: z.string().openapi({ example: '94105' }),
  })
  .openapi('PdfAddress')

// Estimate PDF schemas
export const estimateLineItemSchema = z
  .object({
    name: z.string().openapi({ example: 'Professional Lawn Service' }),
    descriptionHtml: htmlStringSchema.openapi({
      example: '<p>Weekly lawn maintenance including mowing and edging</p>',
    }),
    quantity: z.number().openapi({ example: 4 }),
    unitPriceUsc: uscSchema.openapi({ example: 5000 }),
    photoCdnUrl: bzOptional(z.string().openapi({ example: 'https://cdn.example.com/photos/lawn-service.jpg' })),
    isDiscountable: z.boolean().openapi({ example: true }),
    isTaxable: z.boolean().openapi({ example: true }),
  })
  .openapi('EstimateLineItem')

export const estimateDiscountSchema = z
  .object({
    type: z.enum(['flat', 'rate']),
    name: z.string().openapi({ example: 'Early Payment Discount' }),
    descriptionHtml: htmlStringSchema.openapi({ example: '<p>10% discount for payment within 7 days</p>' }),
    discountRate: bzOptional(z.number().openapi({ example: 0.1 })),
    discountTotalUsc: uscSchema.openapi({ example: 2500 }),
  })
  .openapi('EstimateDiscount')

export const estimateOptionSchema = z
  .object({
    optionGuid: guidSchema,
    seq: z.number().openapi({ example: 1 }),
    displayName: bzOptional(z.string().openapi({ example: 'Standard Package' })),
    descriptionHtml: bzOptional(htmlStringSchema.openapi({ example: '<p>Our most popular service package</p>' })),
    selected: z.boolean().openapi({ example: false }),
    recommended: z.boolean().openapi({ example: true }),
    totalUsc: uscSchema.openapi({ example: 15000 }),
    taxConfiguration: TaxConfigurationSchema,
    lineItems: z.array(estimateLineItemSchema),
    discounts: z.array(estimateDiscountSchema),
    featuredPhotoCdnUrl: bzOptional(z.string().openapi({ example: 'https://cdn.example.com/photos/featured.jpg' })),
  })
  .openapi('EstimateOption')

export const estimateSignatureSchema = z
  .object({
    signatureFileUrl: bzOptional(z.string().openapi({ example: 'https://cdn.example.com/signatures/sig123.png' })),
    acceptedOnBehalfByUser: bzOptional(
      z.object({
        fullName: z.string().openapi({ example: 'Jane Smith' }),
      }),
    ),
    acceptedAt: bzOptional(isoDateStringSchema),
    tzId: timeZoneIdSchema,
    contactFullName: z.string().openapi({ example: 'John Doe' }),
    acceptedOption: bzOptional(
      z.object({
        displayName: z.string().openapi({ example: 'Premium Package' }),
        seq: z.number().openapi({ example: 2 }),
      }),
    ),
  })
  .openapi('EstimateSignature')

export const estimatePdfBillingInfoSchema = z
  .object({
    companyAddressDisplay: z.string().openapi({ example: '123 Business Ave, San Francisco, CA 94105' }),
    companyInfoDisplay: z.string().openapi({ example: 'Phone: (555) 123-4567 | Email: info@company.com' }),
    addressesMatch: bzOptional(z.boolean().openapi({ example: true })),
    businessFullLegalName: bzOptional(z.string().openapi({ example: 'ABC Services LLC' })),
    companyLogoUrl: bzOptional(z.string().openapi({ example: 'https://cdn.example.com/logos/company-logo.png' })),
    serviceAddress: bzOptional(
      z.object({
        contactName: z.string().openapi({ example: 'John Doe' }),
        address: pdfAddressSchema,
      }),
    ),
    billingAddress: bzOptional(
      z.object({
        contactName: z.string().openapi({ example: 'Jane Smith' }),
        address: pdfAddressSchema,
      }),
    ),
  })
  .openapi('EstimatePdfBillingInfo')

const maintenancePlanSavingsSchema = z
  .object({
    potentialSavingsUsc: uscSchema.openapi({ example: 3600 }),
    planName: z.string().openapi({ example: 'Gold Maintenance Plan' }),
    ctaUrl: bzOptional(z.string().openapi({ example: 'https://example.com/maintenance-plan' })),
  })
  .openapi('EstimatePdfMaintenancePlanSavings')

export const CreateEstimatePdfBodySchema = z
  .object({
    messageHtml: bzOptional(htmlStringSchema.openapi({ example: '<p>Thank you for considering our services!</p>' })),
    jobGuid: guidSchema,
    displayId: z.string().openapi({ example: 'EST-2024-001' }),
    technicianName: bzOptional(z.string().openapi({ example: 'Riley M.' })),
    createdAt: isoDateStringSchema,
    status: z.string().openapi({ example: 'pending' }),
    companyName: z.string().openapi({ example: 'ABC Landscaping Services' }),
    disclaimer: z.string().openapi({ example: 'All services subject to terms and conditions' }),
    lineItemVisibilityStatus: z.enum(['HIDDEN', 'VISIBLE']),
    tzId: timeZoneIdSchema,
    taxRate: z.number().openapi({ example: 0.0875 }),
    options: z.array(estimateOptionSchema),
    maintenancePlanSavings: bzOptional(maintenancePlanSavingsSchema),
    signature: estimateSignatureSchema,
    pdfBillingInfo: estimatePdfBillingInfoSchema,
  })
  .openapi('CreateEstimatePdfBody')

export const EstimatePdfResponseSchema = z
  .object({
    estimatePdfBase64: z.string().openapi({ example: 'base64-encoded-pdf' }),
  })
  .openapi('EstimatePdfResponse')

// Type exports
export type PdfAddress = z.infer<typeof pdfAddressSchema>
export type EstimateLineItem = z.infer<typeof estimateLineItemSchema>
export type EstimateDiscount = z.infer<typeof estimateDiscountSchema>
export type EstimateOption = z.infer<typeof estimateOptionSchema>
export type EstimateSignature = z.infer<typeof estimateSignatureSchema>
export type EstimatePdfBillingInfo = z.infer<typeof estimatePdfBillingInfoSchema>
export type CreateEstimatePdfBody = z.infer<typeof CreateEstimatePdfBodySchema>
export type EstimatePdfResponse = z.infer<typeof EstimatePdfResponseSchema>
export type EstimatePdf = CreateEstimatePdfBody
